import React from 'react';
import {
  Search,
  Grid3x3,
  List,
  Upload,
  FolderPlus,
  Filter,
  Menu,
  FolderOpen,
  Palette,
  Image,
  Star,
  FileText,
  Plus,
  File,
  LayoutDashboard,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ViewMode } from '../types';

interface ToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onUpload?: () => void;
  onCreateFolder?: () => void;
  onToggleSidebar?: () => void;
  fileTypeFilter: string;
  onFileTypeFilterChange: (filter: string) => void;
  selectionMode?: boolean;
  selectedItemsCount?: number;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  isGlobalDarkMode?: boolean;
  /** Optional breadcrumb node rendered in the header left side, after the sidebar toggle */
  breadcrumbSlot?: React.ReactNode;
  /** When true, removes the bottom border and uses flat/flush padding (for use inside project tabs) */
  noBorder?: boolean;
}

export interface ViewControlsProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  fileTypeFilter: string;
  onFileTypeFilterChange: (filter: string) => void;
  selectionMode?: boolean;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  isGlobalDarkMode?: boolean;
}

export const ViewControls: React.FC<ViewControlsProps> = ({
  viewMode,
  onViewModeChange,
  fileTypeFilter,
  onFileTypeFilterChange,
  selectionMode = false,
  onSelectAll,
  onDeselectAll,
}) => {
  const [showFilterDropdown, setShowFilterDropdown] = React.useState(false);

  const filterOptions = [
    { value: 'all', label: 'All Files', icon: FolderOpen },
    { value: 'logos', label: 'Logos', icon: Palette },
    { value: 'images', label: 'Images', icon: Image },
    { value: 'icons', label: 'Icons', icon: Star },
    { value: 'documents', label: 'Documents', icon: FileText },
  ];

  const currentFilter = filterOptions.find(option => option.value === fileTypeFilter) || filterOptions[0];

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1 px-4">
      {/* Selection Mode Buttons */}
      {!selectionMode ? (
        <button
          onClick={onSelectAll}
          className="flex items-center justify-center w-8 h-8 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
          title="Select"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      ) : (
        <>
          <button
            onClick={onSelectAll}
            className="flex items-center gap-2 px-3 h-8 rounded-lg border bg-card border-border text-foreground hover:bg-muted transition-colors flex-shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs">Select All</span>
          </button>
          <button
            onClick={onDeselectAll}
            className="flex items-center gap-2 px-3 h-8 rounded-lg border bg-card border-border text-foreground hover:bg-muted transition-colors flex-shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="text-xs">Deselect</span>
          </button>
        </>
      )}

      {/* View Mode Toggle */}
      <div className="relative flex items-center rounded-lg border border-border p-1 bg-card h-8 flex-shrink-0">
        <motion.div
          className="absolute top-1 bottom-1 rounded-sm bg-background shadow-sm"
          initial={false}
          animate={{
            left: viewMode.type === 'grid' ? '4px' : 'calc(50%)',
            width: 'calc(50% - 4px)',
          }}
          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
        />
        <button
          onClick={() => onViewModeChange({ type: 'grid' })}
          className={`relative z-10 px-2.5 h-full rounded-sm transition-colors ${
            viewMode.type === 'grid'
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          title="Grid view"
        >
          <LayoutDashboard className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onViewModeChange({ type: 'list' })}
          className={`relative z-10 px-2.5 h-full rounded-sm transition-colors ${
            viewMode.type === 'list'
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          title="List view"
        >
          <List className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Filter Dropdown */}
      <div className="relative flex-shrink-0 h-8">
        <button
          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          className={`flex items-center justify-center w-8 h-full rounded-lg border transition-colors ${
            fileTypeFilter !== 'all'
              ? 'bg-background shadow-sm text-foreground border-transparent'
              : 'bg-card border-border text-foreground hover:bg-muted'
          }`}
          title={currentFilter.label}
        >
          <Filter size={14} />
        </button>

        <AnimatePresence>
          {showFilterDropdown && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setShowFilterDropdown(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full right-0 mt-1 rounded-xl border bg-popover border-border shadow-lg min-w-[160px] p-1.5 z-40"
              >
                {filterOptions.map((option, index) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onFileTypeFilterChange(option.value);
                      setShowFilterDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${
                      fileTypeFilter === option.value
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <option.icon size={14} />
                      <span>{option.label}</span>
                    </div>
                    {fileTypeFilter === option.value && (
                      <div className="w-1.5 h-1.5 bg-current rounded-full" />
                    )}
                  </button>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

    </div>
  );
};

export const Toolbar: React.FC<ToolbarProps> = ({
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
  onUpload,
  onCreateFolder,
  onToggleSidebar,
  fileTypeFilter,
  onFileTypeFilterChange,
  selectionMode = false,
  selectedItemsCount = 0,
  onSelectAll,
  onDeselectAll,
  breadcrumbSlot,
  noBorder = false,
}) => {
  const [showFilterDropdown, setShowFilterDropdown] = React.useState(false);
  const [showNewDropdown, setShowNewDropdown] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (searchOpen) {
      inputRef.current?.focus();
    }
  }, [searchOpen]);

  const handleSearchClose = () => {
    setSearchOpen(false);
    onSearchChange('');
  };

  const filterOptions = [
    { value: 'all', label: 'All Files', icon: FolderOpen },
    { value: 'logos', label: 'Logos', icon: Palette },
    { value: 'images', label: 'Images', icon: Image },
    { value: 'icons', label: 'Icons', icon: Star },
    { value: 'documents', label: 'Documents', icon: FileText },
  ];

  const currentFilter = filterOptions.find(option => option.value === fileTypeFilter) || filterOptions[0];

  const newOptions = [
    onCreateFolder && {
      id: 'folder',
      label: 'New Folder',
      icon: FolderPlus,
      onClick: onCreateFolder
    },
    onUpload && {
      id: 'file-upload',
      label: 'File Upload',
      icon: Upload,
      onClick: onUpload
    },
  ].filter(Boolean) as { id: string; label: string; icon: React.FC<any>; onClick: () => void }[];
  
  return (
    <div className={noBorder ? 'px-0 py-0' : 'border-b border-border px-6 md:px-10 py-3 lg:py-4'}>
      {/* Desktop Layout: Single Row */}
      <div className="hidden lg:flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-1 mr-4">
          {breadcrumbSlot && (
            <div className="min-w-0 flex-1 overflow-hidden">
              {breadcrumbSlot}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Search — icon-only by default, expands on click */}
          <div className="relative flex items-center h-10">
            <AnimatePresence initial={false}>
              {searchOpen ? (
                <motion.div
                  key="search-open"
                  className="relative flex items-center h-10"
                  initial={{ width: 40, opacity: 0.6 }}
                  animate={{ width: 256, opacity: 1 }}
                  exit={{ width: 40, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Search in Drive"
                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                    className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                  <button
                    onMouseDown={(e) => { e.preventDefault(); handleSearchClose(); }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  key="search-icon"
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Search className="w-4 h-4" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* View Mode Toggle */}
          <div className="relative flex items-center rounded-lg border border-border p-1 bg-card h-10">
            <motion.div
              className="absolute top-1 bottom-1 rounded-sm bg-background shadow-sm"
              initial={false}
              animate={{
                left: viewMode.type === 'grid' ? '4px' : 'calc(50%)',
                width: 'calc(50% - 4px)',
              }}
              transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            />
            <button
              onClick={() => onViewModeChange({ type: 'grid' })}
              className={`relative z-10 px-3 h-full rounded-sm transition-colors ${
                viewMode.type === 'grid'
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
            </button>
            <button
              onClick={() => onViewModeChange({ type: 'list' })}
              className={`relative z-10 px-3 h-full rounded-sm transition-colors ${
                viewMode.type === 'list'
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Filter Dropdown */}
          <div className="relative h-10">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className={`flex items-center space-x-2 px-4 h-full rounded-lg border transition-colors ${
                fileTypeFilter !== 'all'
                  ? 'bg-background shadow-sm text-foreground border-transparent'
                  : 'bg-card border-border text-foreground hover:bg-muted'
              }`}
              title="Filter files"
            >
              <currentFilter.icon className="w-4 h-4" />
              <span className="text-sm font-medium whitespace-nowrap">{currentFilter.label}</span>
            </button>

            <AnimatePresence>
              {showFilterDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowFilterDropdown(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                  >
                    {filterOptions.map((option, index) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          onFileTypeFilterChange(option.value);
                          setShowFilterDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${
                          fileTypeFilter === option.value
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-foreground hover:bg-accent'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <option.icon className="w-4 h-4" />
                          <span>{option.label}</span>
                        </div>
                        {fileTypeFilter === option.value && (
                          <div className="w-2 h-2 bg-current rounded-full" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Selection Mode Buttons */}
          {!selectionMode ? (
            <button
              onClick={onSelectAll}
              className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Select"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          ) : (
            <>
              <button
                onClick={onSelectAll}
                className="flex items-center gap-2 px-4 h-10 rounded-lg border bg-card border-border text-foreground hover:bg-muted transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">Select All</span>
              </button>
              <button
                onClick={onDeselectAll}
                className="flex items-center gap-2 px-4 h-10 rounded-lg border bg-card border-border text-foreground hover:bg-muted transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-sm">Deselect</span>
              </button>
            </>
          )}

          {/* New Button */}
          <div className="relative">
            <button
              onClick={() => setShowNewDropdown(!showNewDropdown)}
              className="flex items-center space-x-2 px-6 h-10 bg-workspace-accent text-white rounded-md transition-colors hover:bg-workspace-accent-hover whitespace-nowrap"
            >
              <Plus className="w-5 h-5" />
              <span>New</span>
            </button>

            <AnimatePresence>
              {showNewDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowNewDropdown(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                  >
                    {newOptions.map((option, index) => (
                      <button
                        key={option.id}
                        onClick={() => {
                          option.onClick();
                          setShowNewDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:bg-accent rounded-lg transition-colors"
                      >
                        <option.icon size={16} />
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile Layout: Single Row (View controls moved to separate component after breadcrumbs) */}
      <div className="lg:hidden">
        <div className="flex items-center gap-2">
          {onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-200 flex-shrink-0"
            title="Toggle sidebar"
          >
            <Menu size={20} />
          </button>
          )}

          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search in Drive"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full h-9 pl-9 pr-4 text-sm rounded-lg border bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none transition-colors"
            />
          </div>

          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowNewDropdown(!showNewDropdown)}
              className="flex items-center justify-center w-9 h-9 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-md transition-colors"
              title="New"
            >
              <Plus size={18} />
            </button>

            <AnimatePresence>
              {showNewDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowNewDropdown(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                  >
                    {newOptions.map((option, index) => (
                      <button
                        key={option.id}
                        onClick={() => {
                          option.onClick();
                          setShowNewDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:bg-accent rounded-lg transition-colors"
                      >
                        <option.icon size={16} />
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

